import { useState, useEffect, useRef } from "react";
import { api, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Upload, Save, Loader2, Home } from "lucide-react";

/* ---------------- shared inputs ---------------- */
const Field = ({ label, value, onChange, textarea, ...rest }) => (
  <label className="block">
    <span className="font-label text-xs font-bold tracking-wide text-ink/60 uppercase">{label}</span>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-white border border-ink/15 rounded-lg px-3 py-2 text-sm text-ink font-body min-h-[80px] focus:border-terracotta"
        {...rest}
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-white border border-ink/15 rounded-lg px-3 py-2 text-sm text-ink font-body focus:border-terracotta"
        {...rest}
      />
    )}
  </label>
);

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

const ImageUpload = ({ url, onChange, className = "" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const u = await uploadFile(file);
      onChange(u);
      toast.success("Imagen subida");
    } catch {
      toast.error("Error al subir la imagen");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`relative ${className}`}>
      <div className="aspect-square w-full rounded-lg overflow-hidden bg-ink/5 border border-ink/15 flex items-center justify-center">
        {url ? <img src={resolveImg(url)} alt="" className="w-full h-full object-cover" /> : <Upload className="text-ink/30" size={22} />}
      </div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        data-testid="upload-btn"
        className="absolute inset-0 flex items-center justify-center bg-ink/0 hover:bg-ink/50 transition-colors text-transparent hover:text-dough font-label text-xs font-bold"
      >
        {busy ? <Loader2 className="animate-spin text-dough" size={18} /> : "Cambiar"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} />
    </div>
  );
};

/* ---------------- login ---------------- */
const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Bienvenido");
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Credenciales inválidas");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dough px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-ink/10 p-8 shadow-xl" data-testid="login-form">
        <h1 className="font-serif text-3xl text-ink mb-1">Panel de Admin</h1>
        <p className="font-body text-ink/50 text-sm mb-6">La Panadería Totopan</p>
        <div className="space-y-4">
          <Field label="Email" value={email} onChange={setEmail} type="email" data-testid="login-email" />
          <Field label="Contraseña" value={password} onChange={setPassword} type="password" data-testid="login-password" />
        </div>
        {err && <p className="text-terracotta text-sm mt-3 font-body" data-testid="login-error">{err}</p>}
        <button
          disabled={busy}
          data-testid="login-submit"
          className="mt-6 w-full bg-terracotta text-dough font-label font-bold rounded-full py-3 hover:bg-ink transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {busy && <Loader2 className="animate-spin" size={16} />} Entrar
        </button>
      </form>
    </div>
  );
};

/* ---------------- dashboard ---------------- */
const CATEGORIES = [
  { id: "guatemalan", label: "Guatemalteco" },
  { id: "steaks", label: "Carnes" },
  { id: "chicken", label: "Pollo" },
  { id: "seafood", label: "Mariscos" },
  { id: "breakfast", label: "Desayuno" },
  { id: "sandwiches", label: "Sándwiches" },
  { id: "soups", label: "Sopas" },
  { id: "pizza", label: "Pizzería" },
  { id: "appetizers", label: "Aperitivos" },
  { id: "salads", label: "Ensaladas" },
  { id: "especiales", label: "Especiales" },
  { id: "sides", label: "Adicionales" },
  { id: "drinks", label: "Bebidas" },
  { id: "bakery", label: "Panadería" },
];

const Dashboard = () => {
  const { logout } = useAuth();
  const [tab, setTab] = useState("general");
  const [settings, setSettings] = useState(null);
  const [menu, setMenu] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [savingS, setSavingS] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => {
      setSettings(data.settings);
      setMenu(data.menu);
      setGallery(data.gallery);
    });
    api.get("/admin/reservations").then(({ data }) => setReservations(data)).catch(() => {});
  }, []);

  if (!settings) return <div className="min-h-screen flex items-center justify-center bg-dough"><Loader2 className="animate-spin text-terracotta" /></div>;

  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setHeroImg = (i, v) => setSettings((s) => {
    const arr = [...(s.hero_images || ["", "", ""])];
    arr[i] = v;
    return { ...s, hero_images: arr };
  });
  const addEventsImg = (url) => setSettings((s) => ({ ...s, events_images: [...(s.events_images || []), url] }));
  const removeEventsImg = (idx) => setSettings((s) => ({ ...s, events_images: (s.events_images || []).filter((_, i) => i !== idx) }));

  const saveSettings = async () => {
    setSavingS(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Cambios guardados");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSavingS(false);
    }
  };

  /* menu ops */
  const addMenu = async () => {
    const { data } = await api.post("/admin/menu", {
      category: "bakery", name_en: "New item", name_es: "Nuevo platillo",
      description_en: "", description_es: "", price: "", image_url: "", order: menu.length + 1,
    });
    setMenu((m) => [...m, data]);
    toast.success("Platillo agregado");
  };
  const updateMenuLocal = (id, k, v) => setMenu((m) => m.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const saveMenu = async (item) => {
    await api.put(`/admin/menu/${item.id}`, item);
    toast.success("Platillo guardado");
  };
  const delMenu = async (id) => {
    await api.delete(`/admin/menu/${id}`);
    setMenu((m) => m.filter((it) => it.id !== id));
    toast.success("Platillo eliminado");
  };

  /* gallery ops */
  const addGallery = async (url) => {
    const { data } = await api.post("/admin/gallery", { image_url: url, caption_en: "", caption_es: "", order: gallery.length + 1 });
    setGallery((g) => [...g, data]);
    toast.success("Foto agregada");
  };
  const delGallery = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    setGallery((g) => g.filter((it) => it.id !== id));
    toast.success("Foto eliminada");
  };

  const delReservation = async (id) => {
    await api.delete(`/admin/reservations/${id}`);
    setReservations((r) => r.filter((it) => it.id !== id));
    toast.success("Reserva eliminada");
  };

  const TABS = [
    { id: "general", label: "General" },
    { id: "menu", label: "Menú" },
    { id: "eventos", label: "Eventos" },
    { id: "reservas", label: `Reservas${reservations.length ? ` (${reservations.length})` : ""}` },
    { id: "gallery", label: "Galería" },
  ];

  return (
    <div className="min-h-screen bg-dough">
      {/* top bar */}
      <div className="sticky top-0 z-20 bg-dough/90 backdrop-blur-xl border-b border-ink/10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={resolveImg(settings.logo_url)} alt="" className="h-9 w-9 rounded-full object-cover" />
            <span className="font-serif text-lg text-ink">Admin · Totopan</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="inline-flex items-center gap-1.5 font-label text-sm font-bold border border-ink/20 rounded-full px-4 py-2 hover:bg-ink hover:text-dough transition-colors" data-testid="view-site">
              <Home size={15} /> Ver sitio
            </a>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-1.5 font-label text-sm font-bold text-terracotta border border-terracotta/40 rounded-full px-4 py-2 hover:bg-terracotta hover:text-dough transition-colors">
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-5 flex gap-1 pb-0">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`tab-${tb.id}`}
              className={`font-label text-sm font-bold px-4 py-2.5 border-b-2 -mb-px transition-colors ${
                tab === tb.id ? "border-terracotta text-ink" : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8">
        {tab === "general" && (
          <div className="space-y-8" data-testid="tab-content-general">
            <Section title="Logo & Hero">
              <div className="grid sm:grid-cols-4 gap-5">
                <div>
                  <span className="font-label text-xs font-bold text-ink/60 uppercase">Logo</span>
                  <ImageUpload url={settings.logo_url} onChange={(u) => setS("logo_url", u)} className="mt-1.5" />
                </div>
                {[0, 1, 2].map((i) => (
                  <div key={i}>
                    <span className="font-label text-xs font-bold text-ink/60 uppercase">Hero {i + 1}</span>
                    <ImageUpload url={(settings.hero_images || [])[i]} onChange={(u) => setHeroImg(i, u)} className="mt-1.5" />
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Textos del Hero">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Título (EN)" value={settings.hero_headline_en} onChange={(v) => setS("hero_headline_en", v)} />
                <Field label="Título (ES)" value={settings.hero_headline_es} onChange={(v) => setS("hero_headline_es", v)} />
                <Field label="Subtítulo (EN)" textarea value={settings.hero_sub_en} onChange={(v) => setS("hero_sub_en", v)} />
                <Field label="Subtítulo (ES)" textarea value={settings.hero_sub_es} onChange={(v) => setS("hero_sub_es", v)} />
                <Field label="Tagline (EN)" value={settings.tagline_en} onChange={(v) => setS("tagline_en", v)} />
                <Field label="Tagline (ES)" value={settings.tagline_es} onChange={(v) => setS("tagline_es", v)} />
              </div>
            </Section>

            <Section title="Sobre Nosotros">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Historia (EN)" textarea value={settings.about_en} onChange={(v) => setS("about_en", v)} />
                <Field label="Historia (ES)" textarea value={settings.about_es} onChange={(v) => setS("about_es", v)} />
              </div>
            </Section>

            <Section title="Contacto & Pedidos">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Teléfono 1" value={settings.phone_1} onChange={(v) => setS("phone_1", v)} />
                <Field label="Teléfono 2" value={settings.phone_2} onChange={(v) => setS("phone_2", v)} />
                <Field label="Dirección" value={settings.address} onChange={(v) => setS("address", v)} />
                <div />
                <Field label="Horario (EN)" value={settings.hours_en} onChange={(v) => setS("hours_en", v)} />
                <Field label="Horario (ES)" value={settings.hours_es} onChange={(v) => setS("hours_es", v)} />
                <Field label="Nota entrega (EN)" value={settings.delivery_note_en} onChange={(v) => setS("delivery_note_en", v)} />
                <Field label="Nota entrega (ES)" value={settings.delivery_note_es} onChange={(v) => setS("delivery_note_es", v)} />
                <Field label="Link DoorDash" value={settings.doordash_url} onChange={(v) => setS("doordash_url", v)} />
                <Field label="Link Uber Eats" value={settings.ubereats_url} onChange={(v) => setS("ubereats_url", v)} />
              </div>
            </Section>

            <Section title="Franja de Promoción (arriba del sitio)">
              <label className="flex items-center gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!settings.promo_enabled}
                  onChange={(e) => setS("promo_enabled", e.target.checked)}
                  data-testid="promo-enabled"
                  className="w-5 h-5 accent-terracotta"
                />
                <span className="font-label text-sm font-bold text-ink">Mostrar franja de promoción</span>
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Texto promo (ES)" value={settings.promo_text_es} onChange={(v) => setS("promo_text_es", v)} />
                <Field label="Texto promo (EN)" value={settings.promo_text_en} onChange={(v) => setS("promo_text_en", v)} />
              </div>
            </Section>

            <button
              onClick={saveSettings}
              disabled={savingS}
              data-testid="save-settings"
              className="inline-flex items-center gap-2 bg-ink text-dough font-label font-bold rounded-full px-8 py-3.5 hover:bg-terracotta transition-colors disabled:opacity-60"
            >
              {savingS ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Guardar cambios
            </button>
          </div>
        )}

        {tab === "menu" && (
          <div className="space-y-5" data-testid="tab-content-menu">
            <button onClick={addMenu} data-testid="add-menu-item" className="inline-flex items-center gap-2 bg-terracotta text-dough font-label font-bold rounded-full px-5 py-2.5 hover:bg-ink transition-colors">
              <Plus size={16} /> Agregar platillo
            </button>
            {menu.map((it) => (
              <div key={it.id} className="bg-white border border-ink/10 rounded-xl p-4 grid md:grid-cols-[110px_1fr_auto] gap-4" data-testid={`menu-row-${it.id}`}>
                <ImageUpload url={it.image_url} onChange={(u) => updateMenuLocal(it.id, "image_url", u)} />
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="font-label text-xs font-bold text-ink/60 uppercase">Categoría</span>
                    <select
                      value={it.category}
                      onChange={(e) => updateMenuLocal(it.id, "category", e.target.value)}
                      className="mt-1.5 w-full bg-white border border-ink/15 rounded-lg px-3 py-2 text-sm font-body"
                    >
                      {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </label>
                  <Field label="Precio" value={it.price} onChange={(v) => updateMenuLocal(it.id, "price", v)} />
                  <label className="block">
                    <span className="font-label text-xs font-bold text-ink/60 uppercase">Etiqueta</span>
                    <select
                      value={it.tag || ""}
                      onChange={(e) => updateMenuLocal(it.id, "tag", e.target.value)}
                      data-testid={`tag-select-${it.id}`}
                      className="mt-1.5 w-full bg-white border border-ink/15 rounded-lg px-3 py-2 text-sm font-body"
                    >
                      <option value="">Ninguna</option>
                      <option value="favorito">★ Favorito</option>
                      <option value="especial">★ Especial</option>
                    </select>
                  </label>
                  <Field label="Nombre (EN)" value={it.name_en} onChange={(v) => updateMenuLocal(it.id, "name_en", v)} />
                  <Field label="Nombre (ES)" value={it.name_es} onChange={(v) => updateMenuLocal(it.id, "name_es", v)} />
                  <Field label="Descripción (EN)" textarea value={it.description_en} onChange={(v) => updateMenuLocal(it.id, "description_en", v)} />
                  <Field label="Descripción (ES)" textarea value={it.description_es} onChange={(v) => updateMenuLocal(it.id, "description_es", v)} />
                </div>
                <div className="flex md:flex-col gap-2">
                  <button onClick={() => saveMenu(it)} data-testid={`save-menu-${it.id}`} className="inline-flex items-center gap-1.5 bg-ink text-dough font-label text-sm font-bold rounded-full px-4 py-2 hover:bg-terracotta transition-colors">
                    <Save size={14} /> Guardar
                  </button>
                  <button onClick={() => delMenu(it.id)} data-testid={`del-menu-${it.id}`} className="inline-flex items-center gap-1.5 text-terracotta border border-terracotta/40 font-label text-sm font-bold rounded-full px-4 py-2 hover:bg-terracotta hover:text-dough transition-colors">
                    <Trash2 size={14} /> Borrar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "eventos" && (
          <div className="space-y-6" data-testid="tab-content-eventos">
            <Section title="Salón de Eventos">
              <label className="flex items-center gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!settings.events_enabled}
                  onChange={(e) => setS("events_enabled", e.target.checked)}
                  data-testid="events-enabled"
                  className="w-5 h-5 accent-terracotta"
                />
                <span className="font-label text-sm font-bold text-ink">Mostrar sección de salón de eventos</span>
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Título (ES)" value={settings.events_title_es} onChange={(v) => setS("events_title_es", v)} />
                <Field label="Título (EN)" value={settings.events_title_en} onChange={(v) => setS("events_title_en", v)} />
                <Field label="Descripción (ES)" textarea value={settings.events_text_es} onChange={(v) => setS("events_text_es", v)} />
                <Field label="Descripción (EN)" textarea value={settings.events_text_en} onChange={(v) => setS("events_text_en", v)} />
              </div>
            </Section>

            <Section title="Fotos del salón">
              <GalleryAdder onAdd={addEventsImg} />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-5">
                {(settings.events_images || []).map((url, idx) => (
                  <div key={idx} className="relative group rounded-xl overflow-hidden border border-ink/10" data-testid={`events-img-${idx}`}>
                    <img src={resolveImg(url)} alt="" className="w-full aspect-square object-cover" />
                    <button
                      onClick={() => removeEventsImg(idx)}
                      data-testid={`del-events-img-${idx}`}
                      className="absolute top-2 right-2 bg-terracotta text-dough rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </Section>

            <button
              onClick={saveSettings}
              disabled={savingS}
              data-testid="save-events"
              className="inline-flex items-center gap-2 bg-ink text-dough font-label font-bold rounded-full px-8 py-3.5 hover:bg-terracotta transition-colors disabled:opacity-60"
            >
              {savingS ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Guardar cambios
            </button>
          </div>
        )}

        {tab === "reservas" && (
          <div className="space-y-4" data-testid="tab-content-reservas">
            {reservations.length === 0 && (
              <p className="font-body text-ink/50 py-10 text-center">Aún no hay solicitudes de reserva.</p>
            )}
            {reservations.map((r) => (
              <div key={r.id} className="bg-white border border-ink/10 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`reservation-${r.id}`}>
                <div className="flex-1 grid sm:grid-cols-2 gap-x-8 gap-y-1">
                  <p className="font-label font-bold text-ink text-lg">{r.name}</p>
                  <p className="font-body text-ink/70 text-sm">📞 <a href={`tel:${(r.phone||'').replace(/[^0-9]/g,'')}`} className="link-underline">{r.phone}</a>{r.email ? ` · ${r.email}` : ""}</p>
                  <p className="font-body text-ink/70 text-sm">🎉 {r.event_type || "—"}</p>
                  <p className="font-body text-ink/70 text-sm">📅 {r.date || "—"} · 👥 {r.guests || "—"}</p>
                  {r.message && <p className="font-body text-ink/60 text-sm sm:col-span-2 mt-1 italic">"{r.message}"</p>}
                </div>
                <button
                  onClick={() => delReservation(r.id)}
                  data-testid={`del-reservation-${r.id}`}
                  className="inline-flex items-center gap-1.5 text-terracotta border border-terracotta/40 font-label text-sm font-bold rounded-full px-4 py-2 hover:bg-terracotta hover:text-dough transition-colors self-start"
                >
                  <Trash2 size={14} /> Borrar
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && (
          <div data-testid="tab-content-gallery">
            <GalleryAdder onAdd={addGallery} />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-6">
              {gallery.map((g) => (
                <div key={g.id} className="relative group rounded-xl overflow-hidden border border-ink/10" data-testid={`gallery-row-${g.id}`}>
                  <img src={resolveImg(g.image_url)} alt="" className="w-full aspect-square object-cover" />
                  <button
                    onClick={() => delGallery(g.id)}
                    data-testid={`del-gallery-${g.id}`}
                    className="absolute top-2 right-2 bg-terracotta text-dough rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-white border border-ink/10 rounded-2xl p-6">
    <h2 className="font-label font-bold text-ink text-lg mb-5">{title}</h2>
    {children}
  </div>
);

const GalleryAdder = ({ onAdd }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file);
      await onAdd(url);
    } catch {
      toast.error("Error al subir");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button onClick={() => ref.current?.click()} data-testid="add-gallery-btn" className="inline-flex items-center gap-2 bg-terracotta text-dough font-label font-bold rounded-full px-5 py-2.5 hover:bg-ink transition-colors">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Agregar foto
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} />
    </>
  );
};

export default function AdminPage() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-dough"><Loader2 className="animate-spin text-terracotta" /></div>;
  if (!user) return <Login />;
  return <Dashboard />;
}
